export type FleetManagerCsvMatchState = 'match' | 'pending' | 'mismatch'

export type FleetManagerCsvMatchCell = {
  value: string
}

export type FleetManagerCsvRow = {
  status: FleetManagerCsvMatchState
  vehicleName: string
  deviceName: string
  targetName: string
  targetSource: string | null
  osCell: FleetManagerCsvMatchCell
  clientCell: FleetManagerCsvMatchCell
  zchipCell: FleetManagerCsvMatchCell
  schipCell: FleetManagerCsvMatchCell
  scriptCell: FleetManagerCsvMatchCell
  lastScriptName: string
  lastScriptTimestamp: string | null
  lastIupName: string
  lastIupTimestamp: string | null
  remediationText: string
  canRemediate: boolean
}

export const FLEET_MANAGER_CSV_HEADERS = [
  'Status',
  'Vehicle',
  'Device',
  'Target',
  'OS',
  'Client',
  'ZChip',
  'SChip',
  'Script',
  'Last Script',
  'Last IUP',
  'Remediation'
] as const

export const DEFAULT_FLEET_MANAGER_CSV_FILENAME = 'fleet-manager-export.csv'

export const STATUS_LABELS: Record<FleetManagerCsvMatchState, string> = {
  match: 'Match',
  pending: 'Update in progress',
  mismatch: 'Mismatch'
}

export const escapeCsvValue = (value: string): string => {
  const escapedValue = value.replace(/"/g, '""')
  return /[",\n\r]/.test(escapedValue) ? `"${escapedValue}"` : escapedValue
}

export const formatTimestampedValue = (name: string, timestamp: string | null): string => {
  return timestamp ? `${name} | ${timestamp}` : name
}

export const formatTargetValue = (row: FleetManagerCsvRow): string => (
  row.targetSource ? `${row.targetName} (${row.targetSource})` : row.targetName
)

export const formatRemediationValue = (row: FleetManagerCsvRow): string => (
  row.canRemediate ? 'Fix' : ''
)

export const mapFleetManagerRowToCsvValues = (row: FleetManagerCsvRow): string[] => [
  STATUS_LABELS[row.status],
  row.vehicleName,
  row.deviceName,
  formatTargetValue(row),
  row.osCell.value,
  row.clientCell.value,
  row.zchipCell.value,
  row.schipCell.value,
  row.scriptCell.value,
  formatTimestampedValue(row.lastScriptName, row.lastScriptTimestamp),
  formatTimestampedValue(row.lastIupName, row.lastIupTimestamp),
  formatRemediationValue(row)
]

export const createFleetManagerCsvContent = (rows: FleetManagerCsvRow[]): string => {
  const headerRow = FLEET_MANAGER_CSV_HEADERS.map((header) => escapeCsvValue(header))
  const dataRows = rows.map((row) =>
    mapFleetManagerRowToCsvValues(row).map((value) => escapeCsvValue(value))
  )

  return [headerRow, ...dataRows].map((row) => row.join(',')).join('\n')
}

export const downloadCsv = (content: string, fileName: string): void => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')

  link.href = url
  link.download = fileName
  link.click()

  URL.revokeObjectURL(url)
}

export const exportFleetManagerRowsToCsv = (
  rows: FleetManagerCsvRow[],
  fileName: string = DEFAULT_FLEET_MANAGER_CSV_FILENAME
): void => {
  const content = createFleetManagerCsvContent(rows)
  downloadCsv(content, fileName)
}
